import {
    View, colors, Grid, Page, GridColumn
} from '@fairygodboss/ui';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import React, {
    useCallback, useEffect, useRef, useState
} from 'react';

import ErrorPage from '../components/ErrorPage/ErrorPage';
import { CommunityContentProvider } from '../context/CommunityContentContext';
import { Provider as CommunityContextProvider } from '../context/CommunityContext';
import { UseModalState } from '../context/ModalContext';
import { PAGE_VIEW_EVENT, fireGTMMetric } from '../utils/Analytics/Google';
import {
    PAGE_VIEW_SECTIONS,
    PAGE_VIEW_TYPES,
    SnowplowPageViewTracker,
} from '../utils/Analytics/SnowPlow/SnowPlowPageViewtracker';
import {
    EmitMetric,
    EmitPage,
} from '../utils/Analytics/VisibilityPixel/VisibilityPixel';
import { DOMAINS } from '../utils/constants';
import getContent from '../utils/getContent';
import { IMAGE_CDN_URL } from '../utils/imageCDN';
import { useRecentEditorial } from '../utils/useRecentEditorial';
import useUser from '../utils/useUser';

import {
    AboveTheFoldLink,
    ContentInfo,
    ContentTitle,
    MainArticleBody,
} from './components';

const BottomConvincer = dynamic(
    () => import('../components/SharedLayout/Convincers/BottomConvincer').then(
        (mod) => mod.BottomConvincer
    ),
    {
        loading: () => <></>,
        ssr: false,
    }
);

const GoogleOneTap = dynamic(
    () => import('../components/Login/GoogleOneTap/GoogleOneTap').then(
        (mod) => mod.default
    ),
    {
        loading: () => <></>,
        ssr: false,
    }
);

const RecentContent = dynamic(
    () => import('./components/RecentContent').then((mod) => mod.RecentContent),
    {
        loading: () => <></>,
        ssr: false,
    }
);

const Footer = dynamic(
    () => import('../components/FooterV2').then((mod) => mod.Footer),
    {
        loading: () => <></>,
        ssr: false,
    }
);

const Comments = dynamic(
    () => import('../components/SharedLayout/Comments/Comments').then(
        (mod) => mod.Comments
    ),
    {
        loading: () => <></>,
        ssr: false,
    }
);

const SocialCTA = dynamic(
    () => import('./components/SocialCTA').then((mod) => mod.SocialCTA),
    {
        loading: () => <></>,
        ssr: false,
    }
);

interface ImageType {
    src?: string;
    alt?: string;
}

interface Logo {
    src: string;
    alt: string;
}

interface PaidCompany {
    companyName: string;
    companyId: number;
    logo: Logo;
    companyUrl: string;
    isSponsor?: boolean;
}

interface UserCommunityData {
    id?: number;
    connectionCount?: number;
    followerCount?: number;
    isVip?: number;
}
interface Image {
    src: string;
    alt?: string;
}

interface Author {
    userId: number;
    username: string;
    hash: string;
    image: Image;
    authorImage: Image;
    authorName: string;
    userCommunityData?: UserCommunityData;
    connectWithMeText: string;
    credential: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface Article {
    article: {
        wink: string;
        contentTitle: string;
        contentDescription: string;
        contentImage?: ImageType;
        isSponsor: number;
        contentBody: string;
        contentBodyImages?: string[];
        description: string;
        metaTitle: string;
        metaDescription: string;
        canonicalUrl: string | null;
    };
    paidCompany: PaidCompany[];
    author: Author;
}

const DefaultAuthor: Author = {
    connectWithMeText: '',
    credential: '',
    hash: '',
    userId: 0,
    username: 'Fairygodboss',
    image: {
        src: '/v1/production/tip/9/owner.webp',
        alt: 'Profile Picture',
    },
    authorImage: {
        src: '/v1/production/tip/9/owner.webp',
        alt: 'Profile Picture',
    },
    authorName: 'Fairygodboss',
};

export const Layout = ({
    article,
    hash,
    type,
    parsedContentBody,
    errorPage,
    urlRedirect,
    pageName,
}) => {
    const {
        setModalOpen,
        setModal,
        setIsPayWall,
        setRegWall,
        setLayoutType,
        setLeadInLogin,
        setAuth0,
        setLeadInPopup,
        setLoginMetrics,
        setTimer,
        modalOpen,
        setErrorPage,
    } = UseModalState();

    if (errorPage) {
        setErrorPage(true);
        return <ErrorPage />;
    }
    if (urlRedirect) {
        setErrorPage(true);
        useEffect(() => {
            window.location.replace(urlRedirect.destination);
        });
        return <></>;
    }

    const [windowWidth, setWindowWidth] = useState<number | null>(905);
    const [backgroundHeight, setBackgroundHeight] = useState<number>(0);
    const [imageHeight, setImageHeight] = useState<number>(0);
    const [fgbId, setFgbId] = useState<string | null>(null);
    const [showGoogleOnetap, setShowGoogleOnetap] = useState<boolean>(false);
    const [isMounted, setIsMounted] = React.useState(false);
    const { user, userLoading, statusLoading } = useUser();
    const { recentContent } = useRecentEditorial();
    const {
        content, isLoading, comments, commentsLoading
    } = getContent(
        type,
        hash,
        false
    );
    const modalTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
        undefined
    );
    const topConvincerRef = useRef<HTMLBodyElement>();
    const articlePictureRef = useRef<HTMLBodyElement>();

    const getUserGTMMetricsMetadata = async () => {
        let userMetadata: {
            fgb_id: string;
            is_bot: boolean;
            is_fgb: boolean;
            is_vip: boolean;
            user_id: number | null;
        } = {
            fgb_id: '',
            is_bot: false,
            is_fgb: false,
            is_vip: false,
            user_id: null,
        };
        if (user.isLoggedIn) {
            userMetadata = {
                ...userMetadata,
                is_fgb: DOMAINS.some((str) => user.email.includes(str)),
                is_vip: !!user.isVip,
                user_id: user.id,
            };
        }
        userMetadata.fgb_id = fgbId;
        userMetadata.is_bot = !!user.isBot;
        return userMetadata;
    };
    React.useEffect(() => {
        setIsMounted(true);
        setWindowWidth(window.innerWidth);
    }, []);
    useEffect(() => {
        let retries = 15;
        let success = false;
        const pollFunction = () => {
            retries -= 1;
            const imgChildren = articlePictureRef?.current
                ? articlePictureRef.current.querySelectorAll('img')
                : [];
            const imgChild = imgChildren[0];
            const height = imgChild?.clientHeight;
            if (height > 0) {
                setImageHeight(height);
                success = true;
            }
            if (retries > 0 || !success) {
                setTimeout(pollFunction, 10);
            }
        };
        pollFunction();
    }, [articlePictureRef, imageHeight]);

    const fireGTMPageViewMetric = async () => {
        if (!content?.article) {
            return;
        }

        const userGTMMetadata = await getUserGTMMetricsMetadata();
        const pageTitle = content.article.contentTitle;
        const pagePath = `/${
            content.type === 'career_topic' ? 'career-topics' : 'articles'
        }/${content.hash}`;
        fireGTMMetric(PAGE_VIEW_EVENT, pagePath, pageTitle, userGTMMetadata);
    };

    React.useEffect(() => {
        if (isMounted && content && !userLoading && !statusLoading && fgbId) {
            fireGTMPageViewMetric();
        }
    }, [statusLoading, userLoading, isMounted, content, fgbId]);

    React.useEffect(() => {
        const firePageData = async () => {
            const companyIds = article.paidCompany?.[0]
                ? article.paidCompany?.map((company) => company.id)
                : [null];
            window.globalPageData = {
                analytics: {
                    metrics: {
                        community_content_key: `editorial-content-${hash}`,
                        company_id: companyIds,
                        user_id: user.id || null,
                        user_email: user.email,
                        user_name: user.username,
                        fgb_id: fgbId,
                    },
                },
            };
            EmitPage();
        };
        if (!userLoading && fgbId) {
            firePageData();
        }
    }, [pageName, hash, article, userLoading, fgbId]);

    useEffect(() => {
        if (!user.isLoggedIn && !userLoading) setShowGoogleOnetap(true);
    }, [userLoading]);

    useEffect(() => {
        if (imageHeight === 0) {
            return;
        }

        const pictureRef = articlePictureRef?.current;
        if (!userLoading && pictureRef) {
            const backgroundOffset = windowWidth < 905 ? 102 : 68;
            const rect = pictureRef.getBoundingClientRect();
            const distanceFromTop = rect.bottom + window.pageYOffset + 30;
            setBackgroundHeight(distanceFromTop - backgroundOffset + 30);

            return;
        }

        if (topConvincerRef?.current && articlePictureRef?.current) {
            const offsetHeight = articlePictureRef?.current.offsetTop;
            const rect = pictureRef.getBoundingClientRect();
            const distanceFromTop = rect.bottom + window.pageYOffset + 30;
            if (windowWidth < 904 && pictureRef) {
                if (offsetHeight && imageHeight) {
                    setBackgroundHeight(distanceFromTop + offsetHeight + 30);
                }
            } else if (offsetHeight) {
                setBackgroundHeight(offsetHeight + 32);
            }
        }
    }, [
        topConvincerRef,
        windowWidth,
        articlePictureRef,
        userLoading,
        imageHeight,
    ]);

    useEffect(() => {
        const interval = setInterval(async () => {
            const Cookies = (await import('js-cookie')).default;
            const newFgbId = Cookies.get('FGB_ID');
            if (newFgbId && newFgbId !== '') {
                setFgbId(newFgbId);
                clearInterval(interval);
            }
        }, 1000);
        return () => clearInterval(interval);
    }, []);
    const windowResize = useCallback(() => {
        const clientWindowWidth = window?.innerWidth;
        if (clientWindowWidth && windowWidth !== clientWindowWidth) {
            setWindowWidth(clientWindowWidth);
        }
        const imgChildren = !articlePictureRef?.current
            ? articlePictureRef?.current?.querySelectorAll('img')
            : [];
        if (imgChildren?.length) {
            const imgChild = imgChildren[0];
            const height = imgChild.clientHeight;
            setImageHeight(height);
        }
    }, [windowWidth]);

    useEffect(() => {
        windowResize();
        window.addEventListener('resize', windowResize, { passive: true });
        return () => {
            window.removeEventListener('resize', windowResize);
        };
    }, [windowResize]);

    useEffect(() => {
        if (modalTimeout) {
            clearTimeout(modalTimeout.current);
        }

        if (!article.isSponsor && !userLoading && user && !user?.isLoggedIn) {
            if (!modalOpen) {
                setTimer(article?.regwall?.delaySeconds ?? 2);
                const timeToOpen = article?.regwall?.delaySeconds
                    ? article.regwall.delaySeconds * 1000
                    : 20000;
                setIsPayWall(article?.regwall?.forceLogin);
                setRegWall(
                    article?.regwall
                        ? {
                            position: article.regwall.position,
                            allowClosing: article.regwall.allowClosing,
                            forceLogin: article.regwall.forceLogin,
                            title: article.regwall.signupCreative.headerText,
                            description:
                                  article.regwall.signupCreative.bodyText,
                            buttonText:
                                  article.regwall.signupCreative.buttonText,
                            overlayColor:
                                  article.regwall.signupCreative.overlayColor,
                            image: article.regwall.signupCreative.image,
                        }
                        : null
                );
                setModal('signin');
                setAuth0('signup-wall');
                setLayoutType(String('create'));
                setLeadInLogin(false);
                setLeadInPopup(true);
                setLoginMetrics({});

                modalTimeout.current = setTimeout(() => {
                    if (!modalOpen) {
                        EmitMetric({
                            misc_event_type: 'signup-wall-viewed',
                            misc_event_count: 1,
                        });
                        setModalOpen(true);
                    }
                }, timeToOpen);
            }
        }
    }, [article, userLoading, modalOpen]);

    useEffect(() => {
        if (modalOpen) clearTimeout(modalTimeout.current);
    }, [modalOpen, userLoading]);

    // const { contentTitle } = article;
    const contentTitle = article.title;
    if (!contentTitle || !parsedContentBody) {
        throw new Error('Article not found ');
    }
    const { paidCompany } = article;
    const { author } = article;
    const contentInfoProps = {
        contentImage: article.contentImage,
        contentTitle,
        author: author || DefaultAuthor,
        ref: articlePictureRef,
        contentCreatedDate: article.createdAt,
        contentModifiedDate: article.modifiedAt,
        paidCompany,
        isSponsor: article.isSponsor,
        topConvincerRef,
        description: article.description,
        hash,
        type,
        isLoading,
        userLoading,
        backgroundHeight,
        user,
    };
    const renderArticleStructuredData = () => {
        const {
            contentImage, createdAt, modifiedAt, metaDescription
        } = article;
        const structuredDataAuthor = author || DefaultAuthor;
        const contentDate = new Date(createdAt).toISOString();
        const parsedContentModifiedDate = new Date(modifiedAt).toISOString();
        const urlType = 'career-topics';
        let nameToUse = structuredDataAuthor.authorName
            || (structuredDataAuthor.active
                ? structuredDataAuthor.username
                : 'Fairygodboss');
        nameToUse = nameToUse || 'Fairygodboss';
        const isOrganization = nameToUse === 'Fairygodboss';
        const authorUrl = structuredDataAuthor.authorHash
            ? `/users/profile/${structuredDataAuthor.authorHash}/${nameToUse}`
            : null;
        const articleJson = {
            '@context': 'http://schema.org',
            '@type': 'Article',
            url: `https://fairygodboss.com/${urlType}/${hash}`,
            headline: contentTitle?.replace(/"/g, '\''),
            image: {
                '@type': 'ImageObject',
                url: contentImage
                    ? `https://d207ibygpg2z1x.cloudfront.net/image/upload${contentImage?.src}`
                    : '',
                width: '669px',
                height: '337px',
            },
            datePublished: contentDate,
            dateModified: parsedContentModifiedDate,
            author: {
                name: nameToUse?.replace(/"/g, '\''),
                type: isOrganization ? 'Organization' : 'Person',
                ...(isOrganization
                    ? { url: 'https://www.fairygodboss.com' }
                    : {}),
                ...(!isOrganization && authorUrl
                    ? { url: `https://fairygodboss.com${authorUrl}` }
                    : {}),
            },
            publisher: {
                '@type': 'Organization',
                name: 'Fairygodboss',
                url: 'https://www.fairygodboss.com',
                logo: {
                    '@type': 'ImageObject',
                    url: 'https://d207ibygpg2z1x.cloudfront.net/image/upload/dpr_2.0,c_scale,g_center,r_max,q_auto,w_48,h_48/v1/production/tip/9/owner.jpg',
                    width: '48px',
                    height: '48px',
                },
                sameAs: [
                    'https://www.facebook.com/fairygodboss/',
                    'https://www.instagram.com/fairygodboss/',
                    'https://twitter.com/fairygodboss/',
                    'https://linkedin.com/company/fairygodboss',
                ],
            },
            description: metaDescription,
            mainEntityOfPage: {
                '@type': 'WebPage',
                '@id': `https://fairygodboss.com/${urlType}/${hash}`,
            },
            isAccessibleForFree: true,
        };
        return (
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify(articleJson),
                }}
            />
        );
    };

    const metaTile = article?.metaTitle && article.metaTitle !== ''
        ? article.metaTitle
        : 'Jobs and company reviews for women | Fairygodboss';

    const metaDescription = article?.metaDescription && article.metaDescription !== ''
        ? article.metaDescription
        : 'Fairygodboss';

    const canonicalUrl = article?.canonicalUrl ? (
        <link rel="canonical" href={article.canonicalUrl} />
    ) : (
        <link
            rel="canonical"
            href={`https://fairygodboss.com/career-topics/${hash}`}
        />
    );
    return (
        <>
            <Head>
                <title>{metaTile}</title>
                <meta
                    property="og:url"
                    content={`https://fairygodboss.com/career-topics/${hash}`}
                />
                <meta property="og:type" content="article" />
                <meta
                    property="og:image"
                    content={`${IMAGE_CDN_URL}/dpr_1,q_auto,c_scale,f_webp,w_669${article.contentImage?.src}`}
                />
                <meta
                    property="og:image:alt"
                    content={article.contentImage?.alt}
                />

                <meta property="og:image:width" content="699" />

                <meta property="og:image:height" content="377" />
                <meta
                    name="twitter:title"
                    content={`${metaTile} | Fairygodboss`}
                    key="twittterTitle"
                />
                <meta
                    name="twitter:description"
                    content={metaDescription}
                    key="twitterDescription"
                />
                <meta
                    name="twitter:Image"
                    content={`${IMAGE_CDN_URL}/dpr_1,q_auto,c_scale,f_webp,w_669${article.contentImage?.src}`}
                    key="twitterImage"
                />
                <meta
                    name="twitter:image:height"
                    content="699"
                    key="twitterImageHeight"
                />
                <meta
                    name="twitter:image:width"
                    content="377"
                    key="twitterImageWidth"
                />
                <meta name="description" content={metaDescription} />
                <meta property="og:title" content={metaTile} />
                <meta property="og:description" content={metaDescription} />
                <meta
                    name="twitter:title"
                    content="Fairygodboss"
                    key="twitterTitle"
                />
                <meta
                    name="twitter:image"
                    content="https://res.cloudinary.com/fairygodboss/image/upload/v1681846562/articles_upload/content/bepbejxmscgrncvwbzsb.webp"
                    key="twitterImage"
                />
                <meta
                    property="og:image"
                    content="https://res.cloudinary.com/fairygodboss/image/upload/v1681846562/articles_upload/content/bepbejxmscgrncvwbzsb.webp"
                />
                {canonicalUrl}
            </Head>
            {renderArticleStructuredData()}
            <View
                display="flex"
                width="100%"
                color={colors.neutral[100]}
                background={`linear-gradient(to bottom, #F6F2FE ${backgroundHeight}px, white 0%);`}
                css={{
                    fontFamily: 'Open Sans',
                }}
            >
                {showGoogleOnetap && (
                    <GoogleOneTap
                        gclientId={
                            process.env.NEXT_PUBLIC_PASSPORT_GOOGLE_CLIENT_ID
                        }
                    />
                )}
                {isMounted && !userLoading && (
                    <SnowplowPageViewTracker
                        pageCompanyId={paidCompany?.[0]?.id ?? null}
                        pageSection={PAGE_VIEW_SECTIONS.ADVICE}
                        pageEntityId={`${type}-${hash}`}
                        pageType={PAGE_VIEW_TYPES.CONTENT}
                        userId={user.id}
                    />
                )}
                <View
                    display="flex"
                    width="100%"
                    flexDirection="column"
                    alignItems="center"
                >
                    <Page width="100%">
                        <Grid
                            maxWidth="1176px"
                            justifyContent={{
                                _: 'left',
                                sm: 'center',
                                md: 'left',
                                lg: 'right',
                            }}
                        >
                            <GridColumn
                                size={{
                                    xs: 12,
                                    sm: 9,
                                    md: 12,
                                    lg: 11,
                                }}
                                pl={{ _: '0px', md: '24px', lg: '8px' }}
                                css={{
                                    paddingLeft: '0px',
                                    paddingRight: '0px',
                                }}
                            >
                                <CommunityContextProvider
                                    singleAttributionContentHash={hash}
                                    attributionMode="single"
                                    content={[content]}
                                >
                                    <AboveTheFoldLink
                                        hash={hash}
                                        isAdmin={user?.isAdmin}
                                        isLoading={isLoading}
                                    />
                                </CommunityContextProvider>

                                <ContentTitle contentTitle={contentTitle} />

                                <ContentInfo {...contentInfoProps} />

                                <MainArticleBody
                                    contentBody={parsedContentBody}
                                    windowWidth={windowWidth}
                                />
                                {!isLoading && content ? (
                                    <CommunityContextProvider
                                        singleAttributionContentHash={hash}
                                        attributionMode="single"
                                        content={[content]}
                                    >
                                        <SocialCTA
                                            content={content}
                                            isLoggedIn={user?.isLoggedIn}
                                        />
                                    </CommunityContextProvider>
                                ) : null}
                            </GridColumn>
                        </Grid>
                        <Grid
                            justifyContent={{
                                _: 'left',
                                sm: 'center',
                                md: 'left',
                                lg: 'right',
                            }}
                            style={{
                                paddingLeft: '0px',
                                paddingRight: '0px',
                            }}
                        >
                            {comments && !commentsLoading ? (
                                <CommunityContextProvider
                                    singleAttributionContentHash={hash}
                                    attributionMode="cherrypick"
                                    content={[comments]}
                                    isLoggedIn={user?.isLoggedIn}
                                >
                                    <Comments
                                        user={user}
                                        hash={comments.hash}
                                    />
                                </CommunityContextProvider>
                            ) : null}
                        </Grid>
                        <CommunityContentProvider
                            content={article}
                            recentContent={recentContent}
                        >
                            {recentContent ? (
                                <RecentContent hash={hash} />
                            ) : null}
                        </CommunityContentProvider>
                        <BottomConvincer
                            jobPage={false}
                            loginTrigger="article_join_footer_button"
                            miscEventType="article_join_footer_button"
                            redirectUrl="/community/feed"
                            alwaysDisplayButton="none"
                            rightAltTag="Fairygodboss members"
                            leftAltTag="Fairygodboss VIP"
                        />
                        <Footer />
                    </Page>
                </View>
            </View>
        </>
    );
};
